/**
 * WHAT: Lists DAMA-linked data sources with classification counts and drill-down to assets.
 * WHY: Data sources bridge DAMA database connections to PRO classification data; admins
 *      need to see which sources have been classified and which still need attention.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Table-based component listing data sources with an asset drill-down modal.
 * WHY: Provides the entry point for admins to inspect classification coverage per
 *      database and drill into individual table/view assets and their sensitivity levels.
 */
const ProDataSources = () => {
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);
    const [assets, setAssets] = useState([]);
    const [showAssetsModal, setShowAssetsModal] = useState(false);

    useEffect(() => {
        fetchSources();
    }, []);

    /* WHAT: Loads all registered data sources from the PRO API.
     * WHY: Populates the main table with source names, types, and classification counts. */
    const fetchSources = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/sources/`);
            setSources(response.data.sources || []);
        } catch (err) {
            console.error('Error fetching sources:', err);
            setError('Failed to load data sources');
        } finally {
            setLoading(false);
        }
    };

    /* WHAT: Fetches assets for a selected source and opens the assets modal.
     * WHY: Admins drill from the sources table into per-source asset detail to check
     *      which tables/views have been classified and at what sensitivity level. */
    const handleViewAssets = async (source) => {
        setSelectedSource(source);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/sources/${source.source_id}/`);
            setAssets(response.data.assets || []);
            setShowAssetsModal(true);
        } catch (err) {
            alert('Failed to load assets');
        }
    };

    /* WHAT: Maps a sensitivity level to its Bootstrap badge color class.
     * WHY: Consistent badge colors across the PRO module reinforce the severity
     *      semantics (red=CRITICAL, yellow=HIGH, blue=MEDIUM, green=LOW). */
    const getSensitivityBadge = (level) => {
        const badges = {
            'CRITICAL': 'bg-danger',
            'HIGH': 'bg-warning text-dark',
            'MEDIUM': 'bg-info',
            'LOW': 'bg-success'
        };
        return badges[level] || 'bg-secondary';
    };

    /* WHAT: Maps a database source type to its Bootstrap badge color class.
     * WHY: Distinct colors per database vendor (red=ORACLE, blue=MSSQL, etc.)
     *      help admins visually distinguish source types in the table. */
    const getSourceTypeBadge = (type) => {
        const badges = {
            'ORACLE': 'bg-danger',
            'MSSQL': 'bg-primary',
            'MYSQL': 'bg-info',
            'POSTGRESQL': 'bg-success',
            'CASSANDRA': 'bg-warning text-dark'
        };
        return badges[type] || 'bg-secondary';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">Data Sources</h4>
                    <p className="text-muted mb-0">
                        Databases with data classification information
                    </p>
                </div>
                <button className="btn btn-outline-primary" onClick={fetchSources}>
                    <i className="fas fa-sync me-2"></i>
                    Refresh
                </button>
            </div>

            {error && (
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </div>
            )}

            <div className="card">
                <div className="card-body p-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Source Name</th>
                                <th>Type</th>
                                <th>Database</th>
                                <th className="text-center">Assets</th>
                                <th className="text-center">Classified</th>
                                <th className="text-center">Users</th>
                                <th>Last Sync</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sources.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-4 text-muted">
                                        No data sources configured. Link sources via MDM connections.
                                    </td>
                                </tr>
                            ) : (
                                sources.map(source => (
                                    <tr key={source.source_id}>
                                        <td>
                                            <strong>{source.source_name}</strong>
                                        </td>
                                        <td>
                                            <span className={`badge ${getSourceTypeBadge(source.source_type)}`}>
                                                {source.source_type}
                                            </span>
                                        </td>
                                        <td>
                                            <code>{source.database_name}</code>
                                            {source.schema_name && (
                                                <span className="text-muted">.{source.schema_name}</span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-primary">{source.asset_count || 0}</span>
                                        </td>
                                        <td className="text-center">
                                            {source.classified_count > 0 ? (
                                                <span className="badge bg-success">{source.classified_count}</span>
                                            ) : (
                                                <span className="badge bg-secondary">0</span>
                                            )}
                                            {source.critical_count > 0 && (
                                                <span className="badge bg-danger ms-1" title="Critical">
                                                    {source.critical_count}
                                                </span>
                                            )}
                                            {source.high_count > 0 && (
                                                <span className="badge bg-warning text-dark ms-1" title="High">
                                                    {source.high_count}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-info">{source.users_with_access || 0}</span>
                                        </td>
                                        <td>
                                            {source.last_catalog_sync_at ? (
                                                <small>{new Date(source.last_catalog_sync_at).toLocaleString()}</small>
                                            ) : (
                                                <span className="text-muted">Never</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleViewAssets(source)}
                                                title="View Assets"
                                            >
                                                <i className="fas fa-table me-1"></i>
                                                View Assets
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Assets Modal */}
            {showAssetsModal && selectedSource && (
                <div className="modal fade show" style={{
                    display: 'block',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 1050,
                    overflow: 'auto'
                }}>
                    <div style={{ padding: '20px' }}>
                        <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-table me-2"></i>
                                    Assets: {selectedSource.source_name}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAssetsModal(false)}></button>
                            </div>
                            <div className="modal-body p-0">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Asset Name</th>
                                            <th>Type</th>
                                            <th>Schema</th>
                                            <th className="text-center">Columns</th>
                                            <th className="text-center">Classified</th>
                                            <th>Max Sensitivity</th>
                                            <th>Owner</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assets.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4 text-muted">
                                                    No assets found
                                                </td>
                                            </tr>
                                        ) : (
                                            assets.map(asset => (
                                                <tr key={asset.asset_id}>
                                                    <td>
                                                        <strong>{asset.asset_name}</strong>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-secondary">{asset.asset_type}</span>
                                                    </td>
                                                    <td>
                                                        <code>{asset.schema_name}</code>
                                                    </td>
                                                    <td className="text-center">{asset.column_count || 0}</td>
                                                    <td className="text-center">
                                                        <span className={`badge ${asset.classified_column_count > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                            {asset.classified_column_count || 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {asset.max_sensitivity_level ? (
                                                            <span className={`badge ${getSensitivityBadge(asset.max_sensitivity_level)}`}>
                                                                {asset.max_sensitivity_level}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {asset.business_owner || asset.technical_owner || '-'}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAssetsModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProDataSources;
